import { useMutation } from "@apollo/client";
import logo from "@static/img/logo-pink-bg.svg";
import { compact } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  DeleteAskAiChatSessionMutationMutation,
  DeleteAskAiChatSessionMutationMutationVariables,
} from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import deleteAskAiChatSessionMutation from "@apps/ask-ai/graphql/delete-ask-ai-chat-session-mutation";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { classNames } from "@helpers/css";
import { getAssetUrl } from "@helpers/helpers";

import AskAI from "../ask-ai/ask-ai";
import ChatHistoryPopover from "./components/chat-history-popover";
import DirectReports from "./components/direct-reports";
import Insights from "./components/insights";
import HomePeople from "./components/people";
import YourIntegrations from "./components/your-integrations";

// Fire-and-forget delete that survives page unload. A normal Apollo mutation
// won't reliably complete while the tab is closing/refreshing, so we POST the
// delete with `fetch keepalive` (sendBeacon can't set the X-CSRFToken header
// the GraphQL endpoint requires). Anything missed is swept by the backend cron.
const beaconDeleteChatSession = (id: number) => {
  try {
    fetch("/graphql", {
      method: "POST",
      keepalive: true,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": window.TF.csrf,
      },
      body: JSON.stringify({
        query: deleteAskAiChatSessionMutation.loc?.source.body,
        variables: { id },
      }),
    }).catch(() => null);
  } catch {
    // best-effort only
  }
};

const Home = () => {
  const { replace } = useLink();
  const params = useParams<{ chatSessionId: string }>();
  const askAIPathName = "/ask-ai";
  const location = useLocation<TFLocationState>();
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const [askAIChatSessionId, setAskAIChatSessionId] = useState<null | number>(
    params.chatSessionId ? parseInt(params.chatSessionId) : null,
  );
  const [startedChatSession, setStartedChatSession] = useState(
    !!askAIChatSessionId || location.pathname.includes(askAIPathName),
  );
  const [isIncognito, setIsIncognito] = useState(false);
  const incognitoSessionIdRef = useRef<number | null>(null);
  const [deleteChatSession] = useMutation<
    DeleteAskAiChatSessionMutationMutation,
    DeleteAskAiChatSessionMutationMutationVariables
  >(deleteAskAiChatSessionMutation);
  useDocumentTitle(startedChatSession ? "Topicflow AI" : "Home");
  const hasDirectReports = currentUser.directReports.edges.length > 0;

  const isAskAIEnabled = !!currentOrganization.featureFlags.askAi;
  // Temporary Chat is an internal tool for Topicflow staff to test prompts
  // while impersonating a user without polluting that user's chat history or
  // AskAI memory. Only surface the toggle while impersonating someone.
  const isImpersonating = window.TF.hijacked;
  const showNewDirectReports = waffle.flag_is_active(
    "new-homepage-direct-reports",
  );
  const showIntegrations = waffle.flag_is_active("onboarding_flow");
  const initialPrompt =
    new URLSearchParams(location.search).get("p") || undefined;
  const isSessionlessAskAIPath =
    location.pathname === askAIPathName ||
    location.pathname === `${askAIPathName}/`;

  // Track the pathname from the previous render so the effect below can tell
  // whether the session id changed (new session created) or the pathname
  // changed (user navigated). See the foreground-rewrite guard for why.
  const prevPathnameRef = useRef(location.pathname);

  const deleteIncognitoSession = useCallback(
    (sessionId = incognitoSessionIdRef.current) => {
      if (!sessionId) return;
      incognitoSessionIdRef.current = null;
      deleteChatSession({ variables: { id: sessionId } }).catch(() => null);
    },
    [deleteChatSession],
  );

  const handleChangeAskAIChatSessionId = useCallback(
    (chatSessionId: number | null) => {
      if (
        isIncognito &&
        incognitoSessionIdRef.current &&
        chatSessionId !== incognitoSessionIdRef.current
      ) {
        deleteIncognitoSession();
        setIsIncognito(false);
      }
      if (isIncognito && chatSessionId) {
        incognitoSessionIdRef.current = chatSessionId;
      }
      setAskAIChatSessionId(chatSessionId);
    },
    [deleteIncognitoSession, isIncognito],
  );

  const handleClickNewChat = useCallback(() => {
    if (isIncognito) {
      deleteIncognitoSession();
      setIsIncognito(false);
    }
    setAskAIChatSessionId(null);
  }, [deleteIncognitoSession, isIncognito]);

  const handleToggleIncognito = useCallback(() => {
    if (isIncognito) {
      deleteIncognitoSession();
      setIsIncognito(false);
      setAskAIChatSessionId(null);
      return;
    }

    setIsIncognito(true);
    setStartedChatSession(true);
    setAskAIChatSessionId(null);
    if (location.pathname !== askAIPathName) {
      replace(askAIPathName);
    }
  }, [deleteIncognitoSession, isIncognito, location.pathname, replace]);

  useEffect(() => {
    return () => deleteIncognitoSession();
  }, [deleteIncognitoSession]);

  // The in-app cleanup above only runs on SPA navigation/unmount. Closing or
  // refreshing the tab tears the page down without unmounting, so also delete
  // the active incognito session on `pagehide` (skip bfcache, which may revive
  // the page). The 24h cron is the final backstop for anything that slips by.
  useEffect(() => {
    const handlePageHide = (event: PageTransitionEvent) => {
      if (event.persisted) return;
      const sessionId = incognitoSessionIdRef.current;
      if (sessionId) beaconDeleteChatSession(sessionId);
    };
    window.addEventListener("pagehide", handlePageHide);
    return () => window.removeEventListener("pagehide", handlePageHide);
  }, []);

  // when the session id changes, update the url
  useEffect(() => {
    const previousPathname = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;

    if (!askAIChatSessionId || isIncognito) return;

    const askAIChatSessionPath = `${askAIPathName}/${askAIChatSessionId}`;
    // A session id just appeared while the pathname stayed put, i.e. a new
    // session was created in place — the user sent the first message from the
    // homepage (/), /dashboard, or the sessionless /ask-ai, all of which embed
    // <AskAI>. In that case we rewrite the URL forward to /ask-ai/<id>.
    //
    // When the pathname CHANGED instead, the session id did not trigger this
    // run — the user navigated (e.g. clicked "new chat" → /ask-ai, or went to
    // /dashboard). The session id in state is stale and about to be cleared by
    // the sibling effect, so rewriting forward would clobber the navigation and
    // undo it. Comparing against the previous pathname distinguishes the two.
    const createdSessionInPlace = previousPathname === location.pathname;
    const background = location.state?.background;
    const backgroundIsAskAI =
      background?.pathname === askAIPathName ||
      background?.pathname.startsWith(`${askAIPathName}/`);
    const shouldUpdateBackground =
      backgroundIsAskAI && background?.pathname !== askAIChatSessionPath;
    const shouldUpdateForeground =
      !background &&
      createdSessionInPlace &&
      // Skip the no-op rewrite when the URL already points at this session
      // (e.g. on initial load of /ask-ai/<id> or after switching sessions).
      location.pathname !== askAIChatSessionPath;

    if (shouldUpdateBackground && background) {
      replace({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: {
          ...location.state,
          background: {
            ...background,
            pathname: askAIChatSessionPath,
          },
        },
      });
    } else if (shouldUpdateForeground) {
      replace(askAIChatSessionPath);
    }
  }, [askAIChatSessionId, isIncognito, location, replace]);

  // when the url changes, update the session id
  useEffect(() => {
    const isAskAIPath = location.pathname.includes(askAIPathName);
    setStartedChatSession(isAskAIPath);

    if (isIncognito && !isAskAIPath) {
      deleteIncognitoSession();
      setIsIncognito(false);
      setAskAIChatSessionId(null);
      return;
    }

    if (params.chatSessionId) {
      const nextChatSessionId = parseInt(params.chatSessionId);
      // Landing on an explicit, persisted session that isn't our tracked
      // incognito session means we've left Temporary Chat: turn it off and
      // clean up the temporary session if one exists. This also covers the
      // case where incognito was toggled on but no session was created yet
      // (incognitoSessionIdRef is null) — deleteIncognitoSession no-ops then.
      if (isIncognito && nextChatSessionId !== incognitoSessionIdRef.current) {
        deleteIncognitoSession();
        setIsIncognito(false);
      }
      if (
        !askAIChatSessionId ||
        params.chatSessionId !== String(askAIChatSessionId)
      ) {
        setAskAIChatSessionId(nextChatSessionId);
      }
    } else if (askAIChatSessionId && !isIncognito) {
      setAskAIChatSessionId(null);
    }
  }, [
    params.chatSessionId,
    location.pathname,
    askAIChatSessionId,
    deleteIncognitoSession,
    isIncognito,
  ]);

  // Prefer URL params (synchronous on navigation) over state (updated
  // asynchronously via useEffect) to avoid a one-frame flicker when
  // switching sessions via the chat-history popover links.
  const effectiveChatSessionId = params.chatSessionId
    ? parseInt(params.chatSessionId)
    : askAIChatSessionId;

  const fullAskAIView =
    isAskAIEnabled && (startedChatSession || !!effectiveChatSessionId);

  const userName = currentUser.firstName || null;
  return (
    <Layout>
      {isAskAIEnabled && (
        <div className="absolute z-1 top-2 right-4 flex items-center gap-4">
          {isSessionlessAskAIPath && isImpersonating && (
            <Button
              onClick={handleToggleIncognito}
              icon={getTopicflowIcon("eyeOff")}
              theme={isIncognito ? buttonTheme.lightBlue : buttonTheme.default}
              tooltip={
                isIncognito ? "Temporary Chat is on" : "Start Temporary Chat"
              }
              aria-label="Temporary Chat"
            />
          )}
          <Button
            to="/ask-ai"
            onClick={handleClickNewChat}
            icon={getTopicflowIcon("pencilSquare")}
          />
          <ChatHistoryPopover />
        </div>
      )}
      <Layout.Container noHeader={fullAskAIView}>
        <Layout.Main
          subClassName={classNames(
            "flex flex-col h-full relative",
            fullAskAIView ? "pb-0" : "",
          )}
          className={classNames(fullAskAIView ? "pt-0 md:pt-0" : "")}
          width={fullAskAIView ? "full-no-padding" : "default"}
        >
          {!fullAskAIView && (
            <div className="mt-0  tf-header-2xl font-semibold tracking-tight flex items-center gap-3 flex-col justify-center">
              <img
                src={getAssetUrl(logo)}
                alt="Topicflow"
                className="block h-10 w-10"
              />
              <div>Welcome back{userName ? `, ${userName}` : ""}!</div>
            </div>
          )}

          {isAskAIEnabled && (
            <AskAI
              isInSidebar={false}
              chatSessionId={effectiveChatSessionId}
              onChangeChatSessionId={handleChangeAskAIChatSessionId}
              onStartChatSession={() => setStartedChatSession(true)}
              className={classNames(!fullAskAIView && "mt-6")}
              isFullScreen={fullAskAIView}
              totalYPaddingAroundContainer={0}
              initialPrompt={initialPrompt}
              isIncognito={isIncognito}
              formButtons={compact([
                {
                  text: hasDirectReports ? "Team Updates" : "My Updates",
                  prompt: hasDirectReports ? "/team-updates " : "/my-updates ",
                  icon: hasDirectReports ? "users" : "user",
                },
              ])}
            />
          )}

          {!fullAskAIView && (
            <div className="py-10 flex flex-col gap-10">
              {showNewDirectReports && <DirectReports />}
              <Insights selectedUser={currentUser} />
              {!showNewDirectReports && <HomePeople />}
              {showIntegrations && (
                <YourIntegrations
                  currentUser={currentUser}
                  currentOrganization={currentOrganization}
                />
              )}
            </div>
          )}
        </Layout.Main>
      </Layout.Container>
    </Layout>
  );
};

export default Home;
